import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Alert,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  PlayArrow as RunIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  CheckCircle,
  EventNote,
  Timer as TimerIcon,
} from '@mui/icons-material';
import { batchJobsAPI, autoSlotsAPI, contactsAPI, bookingTemplatesAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const ACCENT_COLOR = '#FF8C42';
const GRAY_BORDER = '#e0e0e0';
const GRAY_TEXT = '#616161';
const GRAY_TEXT_DARK = '#424242';
const CONTACT_ID_FROM_NAME = /Contact\s+(\d+)/i;

function formatDate(value) {
  if (!value || value === '0001-01-01T00:00:00' || value.startsWith('0001-01-01')) return '—';
  try {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
  } catch {
    return '—';
  }
}

/** GET /customer/booking-templates/template/:contactId — unwrap template object */
function unwrapTemplateFromByContactResponse(response) {
  const raw = response?.data;
  const data = raw?.data ?? raw?.payload ?? raw;
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  return data;
}

function templateOptionLabel(t) {
  if (!t || typeof t !== 'object') return '';
  return String(t.name ?? t.templateName ?? t.title ?? '').trim() || `ID ${t.id ?? ''}`;
}

function templatesForContactSelection(allTemplates, contactId) {
  if (contactId === '' || contactId == null) return [];
  const cid = String(contactId);
  return (allTemplates || []).filter((t) => {
    if (!t || typeof t !== 'object') return false;
    const assigned =
      t.contactId ??
      t.contact_id ??
      (t.contact && typeof t.contact === 'object' ? t.contact.id : undefined);
    if (assigned == null || assigned === '') return false;
    return String(assigned) === cid;
  });
}

function parseContactIdFromName(name) {
  if (!name || typeof name !== 'string') return '';
  const match = CONTACT_ID_FROM_NAME.exec(name);
  return match ? match[1] : '';
}

function getContactIdFromJob(job) {
  if (!job) return '';
  const raw = job.contactId ?? job.ContactId ?? job.contactID;
  if (raw != null && raw !== '') return String(raw);
  return parseContactIdFromName(job.name);
}

export default function BatchJobs() {
  const { t } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const cronPresets = useMemo(
    () => [
      { value: '0 2 * * *', label: t('pages.batchJobs.cron.daily2am') },
      { value: '0 0 * * *', label: t('pages.batchJobs.cron.dailyMidnight') },
      { value: '0 */6 * * *', label: t('pages.batchJobs.cron.every6h') },
      { value: '0 3 * * 0', label: t('pages.batchJobs.cron.weeklySun') },
      { value: '0 2 * * 1-5', label: t('pages.batchJobs.cron.weekdays') },
    ],
    [t]
  );
  const [jobs, setJobs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [runningId, setRunningId] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formErrors, setFormErrors] = useState({});

  const contactTemplateSeq = useRef(0);

  const [formData, setFormData] = useState({
    contactId: '',
    isAutoGenerationEnabled: true,
    generationSchedule: '0 2 * * *',
    daysInAdvance: 30,
    defaultTemplateId: '',
    autoConfirm: false,
    notifyOnGeneration: false,
    notificationEmail: '',
  });

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const res = await batchJobsAPI.getList();
      if (res?.data?.success && Array.isArray(res.data.data)) {
        setJobs(res.data.data);
      } else {
        setJobs([]);
      }
    } catch (err) {
      console.error('Error fetching batch jobs:', err);
      setSnackbar({ open: true, message: t('pages.batchJobs.toast.loadFailed'), severity: 'error' });
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const res = await contactsAPI.getContacts('', '', 0, 500);
      if (res?.data?.success && Array.isArray(res.data.data)) {
        setContacts(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await bookingTemplatesAPI.getTemplates();
      if (res?.data?.success && Array.isArray(res.data.data)) {
        setTemplates(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  useEffect(() => {
    fetchJobs();
    fetchContacts();
    fetchTemplates();
  }, []);

  const filteredTemplates = useMemo(() => {
    return templatesForContactSelection(templates, formData.contactId);
  }, [templates, formData.contactId]);

  const applyContactTemplate = async (contactId) => {
    const seq = ++contactTemplateSeq.current;
    if (!contactId) return;

    try {
      const res = await bookingTemplatesAPI.getTemplateByContactId(contactId);
      if (seq !== contactTemplateSeq.current) return;
      const tmpl = unwrapTemplateFromByContactResponse(res);
      if (tmpl) {
        const tid = tmpl.id ?? tmpl.templateId;
        if (tid != null && tid !== '') {
          setFormData((prev) => ({
            ...prev,
            defaultTemplateId: String(tid),
          }));
        }
      }
    } catch (err) {
      console.error('Error fetching template for contact:', err);
    }
  };

  const contactsById = useMemo(() => {
    const m = new Map();
    contacts.forEach((c) => {
      if (c?.id == null) return;
      const id = c.id;
      m.set(Number(id), c);
      m.set(String(id), c);
    });
    return m;
  }, [contacts]);

  const jobRowTitle = useCallback(
    (job) => {
      const cid = getContactIdFromJob(job);
      if (cid) {
        const c = contactsById.get(Number(cid)) || contactsById.get(String(cid));
        if (c) return c.title || c.name || `Contact #${c.id}`;
      }
      return job?.name || '—';
    },
    [contactsById]
  );

  const handleAdd = () => {
    setEditingJob(null);
    setFormData({
      contactId: '',
      isAutoGenerationEnabled: true,
      generationSchedule: '0 2 * * *',
      daysInAdvance: 30,
      defaultTemplateId: '',
      autoConfirm: false,
      notifyOnGeneration: false,
      notificationEmail: '',
    });
    setFormErrors({});
    setOpenForm(true);
  };


  const handleCloseForm = () => {
    setOpenForm(false);
    setEditingJob(null);
    setFormErrors({});
  };

  const validateForm = useCallback(() => {
    const err = {};
    if (!formData.contactId) err.contactId = t('pages.batchJobs.err.contactRequired');
    if (formData.isAutoGenerationEnabled && !formData.generationSchedule?.trim()) {
      err.generationSchedule = t('pages.batchJobs.err.scheduleRequired');
    }
    if (formData.daysInAdvance != null && (formData.daysInAdvance < 1 || formData.daysInAdvance > 365)) {
      err.daysInAdvance = t('pages.batchJobs.err.daysRange');
    }
    if (!formData.defaultTemplateId) err.defaultTemplateId = t('pages.batchJobs.err.templateRequired');
    const email = formData.notificationEmail?.trim();
    if (email) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        err.notificationEmail = t('pages.batchJobs.err.emailInvalid');
      }
    } else {
      err.notificationEmail = t('pages.batchJobs.err.emailRequired');
    }
    setFormErrors(err);
    return Object.keys(err).length === 0;
  }, [formData, t]);

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const payload = {
        contactId: Number(formData.contactId),
        isAutoGenerationEnabled: Boolean(formData.isAutoGenerationEnabled),
        generationSchedule: formData.generationSchedule?.trim() || '0 2 * * *',
        daysInAdvance: Number(formData.daysInAdvance) || 30,
        defaultTemplateId: Number(formData.defaultTemplateId) || 0,
        autoConfirm: Boolean(formData.autoConfirm),
        notifyOnGeneration: Boolean(formData.notifyOnGeneration),
        notificationEmail: formData.notificationEmail?.trim() || '',
      };
      const res = await autoSlotsAPI.configure(payload);
      if (res?.data?.success) {
        setSnackbar({
          open: true,
          message: res.data.message || t('pages.batchJobs.toast.saveOk'),
          severity: 'success',
        });
        handleCloseForm();
        fetchJobs();
      } else {
        setSnackbar({ open: true, message: res.data?.message || t('pages.batchJobs.toast.saveFail'), severity: 'error' });
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || t('pages.batchJobs.toast.saveErr');
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRun = async (id) => {
    setRunningId(id);
    try {
      const res = await batchJobsAPI.run(id);
      if (res?.data?.success === false) {
        setSnackbar({ open: true, message: res.data?.message || t('pages.batchJobs.toast.runFail'), severity: 'error' });
      } else {
        setSnackbar({ open: true, message: t('pages.batchJobs.toast.runOk'), severity: 'success' });
        fetchJobs();
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || t('pages.batchJobs.toast.runErr');
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setRunningId(null);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newVal = type === 'checkbox' ? checked : value;
    setFormData((prev) => ({
      ...prev,
      [name]: newVal,
    }));
    if (name === 'contactId' && newVal) {
      applyContactTemplate(newVal);
    }
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const totalSlotsGenerated = jobs.reduce((sum, j) => sum + (j.slotsGenerated ?? 0), 0);
  const enabledCount = jobs.filter((j) => j.isEnabled !== false).length;
  const runningCount = jobs.filter((j) => j.status === 'RUNNING').length;

  const renderJobsContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={40} thickness={4} sx={{ color: ACCENT_COLOR }} />
          <Typography variant="body2" sx={{ mt: 2, color: GRAY_TEXT }}>{t('pages.batchJobs.loading')}</Typography>
        </Box>
      );
    }

    if (jobs.length === 0) {
      return (
        <Paper sx={{
          p: 6, textAlign: 'center', borderRadius: 2, border: '1px dashed', borderColor: GRAY_BORDER, bgcolor: '#f5f5f5',
        }}>
          <ScheduleIcon sx={{ fontSize: 64, color: GRAY_TEXT, mb: 2 }} />
          <Typography variant="h6" sx={{ color: GRAY_TEXT_DARK }} gutterBottom>{t('pages.batchJobs.empty.title')}</Typography>
          <Typography variant="body2" sx={{ mb: 2, maxWidth: 400, mx: 'auto', color: GRAY_TEXT }}>
            {t('pages.batchJobs.empty.hint')}
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} sx={{ background: ACCENT_COLOR, '&:hover': { background: ACCENT_COLOR } }}>
            {t('pages.batchJobs.configureAutoSlots')}
          </Button>
        </Paper>
      );
    }

    return (
      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: GRAY_BORDER, overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: isMobile ? 1000 : '100%' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#fafafa' }}>
              <TableCell sx={{ fontWeight: 700, color: GRAY_TEXT_DARK }}>{t('pages.batchJobs.table.slotGeneration')}</TableCell>
              <TableCell sx={{ fontWeight: 700, color: GRAY_TEXT_DARK }}>{t('pages.batchJobs.table.status')}</TableCell>
              <TableCell sx={{ fontWeight: 700, color: GRAY_TEXT_DARK }}>{t('pages.batchJobs.table.enabled')}</TableCell>
              <TableCell sx={{ fontWeight: 700, color: GRAY_TEXT_DARK }}>{t('pages.batchJobs.table.lastRun')}</TableCell>
              <TableCell sx={{ fontWeight: 700, color: GRAY_TEXT_DARK }}>{t('pages.batchJobs.table.nextRun')}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: GRAY_TEXT_DARK }}>{t('pages.batchJobs.table.slotsGenerated')}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: GRAY_TEXT_DARK }}>{t('pages.batchJobs.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((job) => (
              <TableRow key={job.id} hover sx={{ '&:hover': { bgcolor: 'rgba(255, 140, 66, 0.04)' } }}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600} color={GRAY_TEXT_DARK}>{jobRowTitle(job)}</Typography>
                  {job.description && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {job.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={job.status || 'UNKNOWN'}
                    sx={{
                      fontWeight: 600,
                      ...(job.status === 'RUNNING' && { bgcolor: '#e6fae6', color: '#38963c', border: '1px solid rgba(56, 150, 60, 0.3)' }),
                      ...(job.status !== 'RUNNING' && { bgcolor: '#f5f5f5', color: GRAY_TEXT, border: '1px solid ' + GRAY_BORDER }),
                    }}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  {job.isEnabled ? (
                    <Chip size="small" label={t('common.yes')} sx={{ bgcolor: '#e6fae6', color: '#38963c', fontWeight: 600 }} />
                  ) : (
                    <Chip size="small" label={t('common.no')} sx={{ bgcolor: '#f5f5f5', color: GRAY_TEXT, fontWeight: 600 }} />
                  )}
                </TableCell>
                <TableCell sx={{ color: GRAY_TEXT }}>{formatDate(job.lastRun)}</TableCell>
                <TableCell sx={{ color: GRAY_TEXT }}>{formatDate(job.nextRun)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: GRAY_TEXT_DARK }}>{job.slotsGenerated ?? 0}</TableCell>
                <TableCell align="right">
                  <Tooltip title={t('pages.batchJobs.tooltip.run')}>
                    <IconButton
                      size="small"
                      onClick={() => handleRun(job.id)}
                      disabled={runningId === job.id || job.status === 'RUNNING'}
                      sx={{ color: ACCENT_COLOR, '&:hover': { bgcolor: 'rgba(255, 140, 66, 0.08)' } }}
                    >
                      {runningId === job.id ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <RunIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: 1400, mx: 'auto', pr: { xs: 1, sm: 2 } }}>
      <Box sx={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center',
        justifyContent: 'space-between', gap: 2, mb: 4, p: { xs: 2, sm: 4 },
        borderRadius: { xs: 2, sm: 4 },
        background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
        border: '1px solid rgba(255, 140, 66, 0.2)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      }}>
        <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 800, letterSpacing: '-0.03em', color: GRAY_TEXT_DARK }}>
            {t('pages.batchJobs.title')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 500, color: GRAY_TEXT }}>
            {t('pages.batchJobs.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            fullWidth={isMobile}
            startIcon={<RefreshIcon />}
            onClick={fetchJobs}
            disabled={loading}
            sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 600, borderColor: GRAY_BORDER, color: GRAY_TEXT_DARK, '&:hover': { borderColor: ACCENT_COLOR, color: ACCENT_COLOR } }}
          >
            {t('pages.batchJobs.refresh')}
          </Button>
          <Button
            variant="contained"
            fullWidth={isMobile}
            startIcon={<AddIcon sx={{ fontSize: 22 }} />}
            onClick={handleAdd}
            sx={{
              px: 3.5, py: 1.4, borderRadius: '14px', textTransform: 'none', fontWeight: 700, fontSize: '0.97rem',
              background: ACCENT_COLOR, boxShadow: '0 4px 18px rgba(255, 140, 66, 0.4)',
              '&:hover': { background: '#E06526', boxShadow: '0 8px 28px rgba(255, 140, 66, 0.5)', transform: 'translateY(-2px) scale(1.025)' },
            }}
          >
            {t('pages.batchJobs.configureAutoSlots')}
          </Button>
        </Box>
      </Box>

      {/* Summary Cards — light pastel colors */}
      {!loading && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={6} sm={6} md={3}>
            <Paper elevation={0} sx={{
              padding: { xs: '16px', sm: '24px' }, height: '95%', borderRadius: 2, position: 'relative', overflow: 'hidden',
              bgcolor: '#e8ecf8', border: '1px solid rgba(52, 73, 142, 0.1)',
            }}>
              <EventNote sx={{ position: 'absolute', right: -10, bottom: -10, fontSize: { xs: 60, sm: 100 }, opacity: 0.08, color: '#34498e' }} />
              <Typography variant={isMobile ? 'h5' : 'h3'} sx={{ fontWeight: 800, mb: 1, color: '#34498e' }}>{jobs.length}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#34498e', display: 'block', lineHeight: 1.2 }}>{t('pages.batchJobs.stats.totalJobs')}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Paper elevation={0} sx={{
              padding: { xs: '16px', sm: '24px' }, height: '95%', borderRadius: 2, position: 'relative', overflow: 'hidden',
              bgcolor: '#e6fae6', border: '1px solid rgba(56, 150, 60, 0.1)',
            }}>
              <CheckCircle sx={{ position: 'absolute', right: -10, bottom: -10, fontSize: { xs: 60, sm: 100 }, opacity: 0.08, color: '#38963c' }} />
              <Typography variant={isMobile ? 'h5' : 'h3'} sx={{ fontWeight: 800, mb: 1, color: '#38963c' }}>{enabledCount}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#38963c', display: 'block', lineHeight: 1.2 }}>{t('pages.batchJobs.stats.enabled')}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Paper elevation={0} sx={{
              padding: { xs: '16px', sm: '24px' }, height: '95%', borderRadius: 2, position: 'relative', overflow: 'hidden',
              bgcolor: '#fff7e6', border: '1px solid rgba(240, 150, 48, 0.1)',
            }}>
              <ScheduleIcon sx={{ position: 'absolute', right: -10, bottom: -10, fontSize: { xs: 60, sm: 100 }, opacity: 0.08, color: '#f09630' }} />
              <Typography variant={isMobile ? 'h5' : 'h3'} sx={{ fontWeight: 800, mb: 1, color: '#f09630' }}>{runningCount}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#f09630', display: 'block', lineHeight: 1.2 }}>{t('pages.batchJobs.stats.running')}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Paper elevation={0} sx={{
              padding: { xs: '16px', sm: '24px' }, height: '95%', borderRadius: 2, position: 'relative', overflow: 'hidden',
              bgcolor: '#eaf5ff', border: '1px solid rgba(52, 73, 142, 0.1)',
            }}>
              <TimerIcon sx={{ position: 'absolute', right: -10, bottom: -10, fontSize: { xs: 60, sm: 100 }, opacity: 0.08, color: '#34498e' }} />
              <Typography variant={isMobile ? 'h5' : 'h3'} sx={{ fontWeight: 800, mb: 1, color: '#34498e' }}>{totalSlotsGenerated}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#34498e', display: 'block', lineHeight: 1.2 }}>{t('pages.batchJobs.stats.slotsGenerated')}</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {renderJobsContent()}

      {/* Add/Edit Form Dialog */}
      <Dialog
        open={openForm}
        onClose={handleCloseForm}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            boxShadow: '0 8px 40px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.25rem', color: GRAY_TEXT_DARK, borderBottom: '1px solid', borderColor: GRAY_BORDER, py: 2 }}>
          {t('pages.batchJobs.dialog.createTitle')}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required error={Boolean(formErrors.contactId)}>
                <InputLabel>{t('pages.batchJobs.form.contact')}</InputLabel>
                <Select
                  name="contactId"
                  value={formData.contactId}
                  onChange={handleChange}
                  label={t('pages.batchJobs.form.contact')}
                  disabled={!!editingJob}
                >
                  <MenuItem value="">{t('pages.batchJobs.form.selectContact')}</MenuItem>
                  {contacts.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                      {c.title || c.name || `Contact #${c.id}`}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.contactId && <FormHelperText>{formErrors.contactId}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    name="isAutoGenerationEnabled"
                    checked={formData.isAutoGenerationEnabled}
                    onChange={handleChange}
                  />
                }
                label={t('pages.batchJobs.form.autoGenEnabled')}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required error={Boolean(formErrors.generationSchedule)}>
                <InputLabel>{t('pages.batchJobs.form.scheduleCron')}</InputLabel>
                <Select
                  name="generationSchedule"
                  value={formData.generationSchedule}
                  onChange={handleChange}
                  label={t('pages.batchJobs.form.scheduleCron')}
                >
                  {cronPresets.map((p) => (
                    <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                name="daysInAdvance"
                label={t('pages.batchJobs.form.daysInAdvance')}
                type="number"
                value={formData.daysInAdvance}
                onChange={handleChange}
                error={Boolean(formErrors.daysInAdvance)}
                helperText={formErrors.daysInAdvance}
                inputProps={{ min: 1, max: 365 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={Boolean(formErrors.defaultTemplateId)}>
                <InputLabel>{t('pages.batchJobs.form.defaultTemplate')}</InputLabel>
                <Select
                  name="defaultTemplateId"
                  value={formData.defaultTemplateId}
                  onChange={handleChange}
                  label={t('pages.batchJobs.form.defaultTemplate')}
                >
                  <MenuItem value="">{t('pages.batchJobs.form.selectTemplate')}</MenuItem>
                  {filteredTemplates.map((t) => (
                    <MenuItem key={t.id} value={String(t.id)}>
                      {templateOptionLabel(t)}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.defaultTemplateId && <FormHelperText>{formErrors.defaultTemplateId}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    name="autoConfirm"
                    checked={formData.autoConfirm}
                    onChange={handleChange}
                  />
                }
                label={t('pages.batchJobs.form.autoConfirm')}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    name="notifyOnGeneration"
                    checked={formData.notifyOnGeneration}
                    onChange={handleChange}
                  />
                }
                label={t('pages.batchJobs.form.notifyOnGen')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                name="notificationEmail"
                label={t('pages.batchJobs.form.notificationEmail')}
                type="email"
                value={formData.notificationEmail}
                onChange={handleChange}
                error={Boolean(formErrors.notificationEmail)}
                helperText={formErrors.notificationEmail}
                placeholder={t('pages.batchJobs.form.emailPlaceholder')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 2, borderTop: '1px solid', borderColor: GRAY_BORDER, bgcolor: '#fafafa', flexWrap: 'wrap', justifyContent: isMobile ? 'center' : 'flex-end' }}>
          <Button onClick={handleCloseForm} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, background: ACCENT_COLOR, '&:hover': { background: '#E06526' } }}>
            {submitting ? <CircularProgress size={20} color="inherit" /> : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
